import React, { useState, useEffect, useMemo } from 'react';
import {
  CreditCard, Upload, RefreshCw, Plus, ChevronDown, ChevronRight,
  Check, X, AlertTriangle, FileText, Link2, Search, Filter,
  Download, Trash2, Eye, ArrowLeftRight, Gauge, TrendingUp, Fuel
} from 'lucide-react';
import { fuelCardAPI } from '../../api/operations.js';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { currencySymbol } from '../../utils/formatMoney.js';
import api from '../../api/index.js';
import { useFormat } from '../../i18n/format.js';

// Provider configuration — le libellé vient de i18n (fuel.cards.providers.<code>)
const PROVIDER_CONFIG = {
  totalenergies: { code: 'totalenergies', color: 'bg-red-100 text-red-800' },
  dkv: { code: 'dkv', color: 'bg-blue-100 text-blue-800' },
  shell: { code: 'shell', color: 'bg-yellow-100 text-yellow-800' },
  uta_edenred: { code: 'uta_edenred', color: 'bg-purple-100 text-purple-800' },
  esso_wex: { code: 'esso_wex', color: 'bg-indigo-100 text-indigo-800' },
  leclerc_pro: { code: 'leclerc_pro', color: 'bg-green-100 text-green-800' },
  other: { code: 'other', color: 'bg-gray-100 text-gray-800' },
};

// Le libellé vient de i18n (fuel.cards.reconciliation.<code>)
const RECONCILIATION_STATUS = {
  unmatched: { code: 'unmatched', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  auto_matched: { code: 'auto_matched', color: 'bg-blue-100 text-blue-800', icon: Link2 },
  manual_matched: { code: 'manual_matched', color: 'bg-teal-100 text-teal-800', icon: Link2 },
  confirmed: { code: 'confirmed', color: 'bg-green-100 text-green-800', icon: Check },
  rejected: { code: 'rejected', color: 'bg-red-100 text-red-800', icon: X },
  duplicate: { code: 'duplicate', color: 'bg-gray-100 text-gray-800', icon: X },
};

// Argent : un seul outil (utils/formatMoney via useOrgCurrency). Le backend rend
// déjà la devise du domaine dans `stats.currency` (cf. fuel_card_routes.mjs).

function FuelCardView({ onMessage }) {
  const { t, fmt } = useFormat();
  const [activeTab, setActiveTab] = useState('cards'); // cards | transactions | import | stats
  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [stats, setStats] = useState(null);
  // Devise du domaine cartes : celle que le backend a déjà résolue (org unique) ou
  // la devise du périmètre en consolidé.
  const { money, scopeCurrency } = useOrgCurrency();
  const cardsCurrency = stats?.currency || scopeCurrency;
  const [search, setSearch] = useState('');

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importProvider, setImportProvider] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Reconciliation
  const [reconciling, setReconciling] = useState(false);

  // Card form
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardForm, setCardForm] = useState({
    card_number: '', provider: '', vehicle_id: '', driver_id: '',
    card_type: 'vehicle', expiry_date: '', monthly_limit: '', notes: ''
  });

  // Consumption data + lookups
  const [consumption, setConsumption] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  // Filter
  const [txFilter, setTxFilter] = useState('all'); // all | unmatched | matched | anomaly

  const loadData = async () => {
    setLoading(true);
    try {
      const [cardsRes, txRes, importsRes, statsRes, consumRes, driversRes, vehiclesRes] = await Promise.all([
        fuelCardAPI.getAll(),
        fuelCardAPI.getTransactions(),
        fuelCardAPI.getImportHistory(),
        fuelCardAPI.getStats(),
        fuelCardAPI.getConsumption().catch(() => ({ data: [] })),
        api.driver.getAll().catch(() => ({ data: [] })),
        api.vehicle.getAll().catch(() => ({ data: [] })),
      ]);
      setCards(cardsRes?.data || []);
      setTransactions(txRes?.data || []);
      setImportHistory(importsRes?.data || []);
      setStats(statsRes?.data || null);
      setConsumption(consumRes?.data || []);
      setDrivers(driversRes?.data || []);
      setVehicles(vehiclesRes?.data || []);
    } catch (err) {
      console.error('[FuelCardView] load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Filtered transactions
  const filteredTx = useMemo(() => {
    let list = transactions;
    if (txFilter === 'unmatched') list = list.filter(t => t.reconciliation_status === 'unmatched');
    else if (txFilter === 'matched') list = list.filter(t => ['auto_matched', 'manual_matched', 'confirmed'].includes(t.reconciliation_status));
    else if (txFilter === 'anomaly') list = list.filter(t => t.is_anomaly);

    if (search.length >= 2) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.station_name?.toLowerCase().includes(q) ||
        t.vehicle_registration?.toLowerCase().includes(q) ||
        t.driver_name?.toLowerCase().includes(q) ||
        t.card_number?.toLowerCase().includes(q) ||
        t.station_city?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [transactions, txFilter, search]);

  // Build consumption lookup by card_id
  const consumptionMap = useMemo(() => {
    const map = {};
    consumption.forEach(c => { map[c.card_id] = c; });
    return map;
  }, [consumption]);

  // Driver/Vehicle lookup maps
  const driverMap = useMemo(() => {
    const map = {};
    drivers.forEach(d => { map[d.id] = d; });
    return map;
  }, [drivers]);

  const vehicleMap = useMemo(() => {
    const map = {};
    vehicles.forEach(v => { map[v.id] = v; });
    return map;
  }, [vehicles]);

  // === IMPORT CSV ===
  const handleImport = async () => {
    if (!importFile || !importProvider) {
      onMessage?.({ title: t('fuel.messages.errorTitle'), message: t('fuel.cards.messages.selectProviderAndFile'), type: 'error' });
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const res = await fuelCardAPI.importCSV(importFile, importProvider);
      if (res?.ok) {
        setImportResult(res.data);
        onMessage?.({
          title: t('fuel.cards.messages.importSuccessTitle'),
          message: t('fuel.cards.messages.importSuccess', { imported: res.data.imported, total: res.data.total_rows }),
          type: 'success'
        });
        setImportFile(null);
        loadData();
      } else {
        onMessage?.({ title: t('fuel.messages.errorTitle'), message: res?.error || t('fuel.cards.messages.importFailed'), type: 'error' });
      }
    } catch (err) {
      onMessage?.({ title: t('fuel.messages.errorTitle'), message: err.message, type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  // === RECONCILIATION ===
  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const res = await fuelCardAPI.reconcile(80);
      if (res?.ok) {
        onMessage?.({
          title: t('fuel.cards.messages.reconcileDoneTitle'),
          message: t('fuel.cards.messages.reconcileDone', { count: res.data.matched, total: res.data.total }),
          type: 'success'
        });
        loadData();
      } else {
        onMessage?.({ title: t('fuel.messages.errorTitle'), message: res?.error || t('fuel.cards.messages.reconcileFailed'), type: 'error' });
      }
    } catch (err) {
      onMessage?.({ title: t('fuel.messages.errorTitle'), message: err.message, type: 'error' });
    } finally {
      setReconciling(false);
    }
  };

  // === CREATE CARD ===
  const handleCreateCard = async () => {
    if (!cardForm.card_number || !cardForm.provider) {
      onMessage?.({ title: t('fuel.messages.errorTitle'), message: t('fuel.cards.messages.cardFieldsRequired'), type: 'error' });
      return;
    }
    try {
      const res = await fuelCardAPI.create(cardForm);
      if (res?.ok) {
        onMessage?.({ title: t('fuel.cards.messages.cardCreatedTitle'), message: t('fuel.cards.messages.cardCreated', { number: cardForm.card_number }), type: 'success' });
        setShowCardForm(false);
        setCardForm({ card_number: '', provider: '', vehicle_id: '', driver_id: '', card_type: 'vehicle', expiry_date: '', monthly_limit: '', notes: '' });
        loadData();
      }
    } catch (err) {
      onMessage?.({ title: t('fuel.messages.errorTitle'), message: err.message, type: 'error' });
    }
  };

  const handleDeleteCard = async (id) => {
    if (!window.confirm(t('fuel.cards.messages.confirmDeleteCard'))) return;
    try {
      await fuelCardAPI.delete(id);
      onMessage?.({ title: t('fuel.cards.messages.cardDeletedTitle'), type: 'success' });
      loadData();
    } catch (err) {
      onMessage?.({ title: t('fuel.messages.errorTitle'), message: err.message, type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('fuel.cards.loading')}</div>
      </div>
    );
  }

  const tabs = [
    { id: 'cards', label: t('fuel.cards.tabs.cards'), icon: CreditCard, count: cards.length },
    { id: 'transactions', label: t('fuel.cards.tabs.transactions'), icon: FileText, count: transactions.length },
    { id: 'import', label: t('fuel.cards.tabs.import'), icon: Upload },
    { id: 'stats', label: t('fuel.cards.tabs.stats'), icon: ArrowLeftRight },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-6 h-6 text-blue-600" />
              {t('fuel.cards.title')}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('fuel.cards.subtitle')}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2 disabled:opacity-50"
              title={t('fuel.cards.reconcileHint')}
            >
              <Link2 className={`w-4 h-4 ${reconciling ? 'animate-spin' : ''}`} />
              {t('fuel.cards.reconcile')}
            </button>
            <button
              onClick={loadData}
              className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'cards' && renderCardsTab()}
      {activeTab === 'transactions' && renderTransactionsTab()}
      {activeTab === 'import' && renderImportTab()}
      {activeTab === 'stats' && renderStatsTab()}
    </div>
  );

  // =========================================================================
  // CONSUMPTION GAUGE (reusable)
  // =========================================================================
  function ConsumptionGauge({ consumed, limit, compact, row }) {
    const amount = (v) => money(v, row || cardsCurrency);
    if (!limit || limit <= 0) {
      return consumed > 0
        ? <span className="text-sm text-gray-500 font-medium">{t('fuel.cards.gauge.spent', { amount: amount(consumed) })}</span>
        : <span className="text-xs text-gray-400">{t('fuel.cards.gauge.noLimit')}</span>;
    }
    const pct = Math.round((consumed / limit) * 100);
    const over = consumed > limit;
    const overAmount = consumed - limit;
    const barPct = Math.min(pct, 100);
    const color = over ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : pct >= 50 ? 'bg-yellow-400' : 'bg-green-500';

    return (
      <div className={compact ? 'w-28' : 'w-full'}>
        <div className="flex items-center justify-between text-xs mb-0.5">
          <span className={`font-semibold ${over ? 'text-red-700' : pct >= 70 ? 'text-orange-700' : 'text-gray-700'}`}>
            {amount(consumed)} / {amount(limit)}
          </span>
          {over ? (
            <span className="font-bold text-red-600">+{amount(overAmount)}</span>
          ) : (
            <span className="text-gray-400">{t('fuel.cards.gauge.remaining', { amount: amount(limit - consumed) })}</span>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 relative">
          <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${barPct}%` }} />
          {over && (
            <div className="absolute right-0 top-0 h-2 bg-red-300 rounded-r-full animate-pulse" style={{ width: `${Math.min((overAmount / limit) * 100, 40)}%` }} />
          )}
        </div>
      </div>
    );
  }

  // =========================================================================
  // CARDS TAB — Table layout with consumption gauge
  // =========================================================================
  function renderCardsTab() {
    const STATUS_COLOR = { active: 'bg-green-100 text-green-800', blocked: 'bg-red-100 text-red-800', expired: 'bg-orange-100 text-orange-800' };

    return (
      <div className="space-y-3">
        {/* Add card button */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowCardForm(!showCardForm)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('fuel.cards.addCard')}
          </button>
        </div>

        {/* Card form */}
        {showCardForm && (
          <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-6">
            <h3 className="text-lg font-semibold mb-4">{t('fuel.cards.form.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.form.cardNumber')}</label>
                <input type="text" value={cardForm.card_number} onChange={e => setCardForm({ ...cardForm, card_number: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="7036 xxxx xxxx 1234" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.form.provider')}</label>
                <select value={cardForm.provider} onChange={e => setCardForm({ ...cardForm, provider: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="">{t('fuel.cards.form.providerPlaceholder')}</option>
                  {Object.keys(PROVIDER_CONFIG).map(k => <option key={k} value={k}>{t(`fuel.cards.providers.${k}`)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.form.type')}</label>
                <select value={cardForm.card_type} onChange={e => setCardForm({ ...cardForm, card_type: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="vehicle">{t('fuel.cards.cardTypes.vehicle')}</option>
                  <option value="driver">{t('fuel.cards.cardTypes.driver')}</option>
                  <option value="pool">{t('fuel.cards.cardTypes.pool')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.form.expiryDate')}</label>
                <input type="date" value={cardForm.expiry_date} onChange={e => setCardForm({ ...cardForm, expiry_date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.form.monthlyLimit', { currency: cardsCurrency })}</label>
                <input type="number" value={cardForm.monthly_limit} onChange={e => setCardForm({ ...cardForm, monthly_limit: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.form.notes')}</label>
                <input type="text" value={cardForm.notes} onChange={e => setCardForm({ ...cardForm, notes: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button onClick={() => setShowCardForm(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-300">{t('common.actions.cancel')}</button>
              <button onClick={handleCreateCard} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">{t('fuel.cards.form.submit')}</button>
            </div>
          </div>
        )}

        {/* Cards table */}
        {cards.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
            <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{t('fuel.cards.emptyTitle')}</p>
            <p className="text-sm mt-1">{t('fuel.cards.emptyHint')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.table.card')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.table.provider')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.table.assignment')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.table.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-56">{t('fuel.cards.table.budget')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.table.expires')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {cards.map(card => {
                  const prov = PROVIDER_CONFIG[card.provider] || PROVIDER_CONFIG.other;
                  const cData = consumptionMap[card.id];
                  return (
                    <tr key={card.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="font-mono text-sm font-medium text-gray-900">{card.card_number}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${prov.color}`}>{t(`fuel.cards.providers.${prov.code}`)}</span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {card.driver_id && driverMap[card.driver_id] ? (
                          <span className="text-gray-900">{driverMap[card.driver_id].first_name} {driverMap[card.driver_id].last_name}</span>
                        ) : card.vehicle_id && vehicleMap[card.vehicle_id] ? (
                          <span className="text-gray-900">{vehicleMap[card.vehicle_id].license_plate}</span>
                        ) : (
                          <span className="text-orange-500 italic text-xs">{t('fuel.cards.unassigned')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${STATUS_COLOR[card.status] || 'bg-gray-100 text-gray-800'}`}>
                          {t(`fuel.cards.status.${card.status}`, { defaultValue: card.status })}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ConsumptionGauge
                          consumed={cData?.consumed || 0}
                          limit={cData?.monthly_limit || parseFloat(card.monthly_limit) || 0}
                          row={card}
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{card.expiry_date || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => handleDeleteCard(card.id)} className="p-1 text-red-400 hover:text-red-600" title={t('common.actions.delete')}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // TRANSACTIONS TAB
  // =========================================================================
  function renderTransactionsTab() {
    return (
      <div className="space-y-3">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('fuel.cards.tx.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex gap-1">
            {[
              { id: 'all', label: t('fuel.cards.tx.filters.all') },
              { id: 'unmatched', label: t('fuel.cards.tx.filters.unmatched') },
              { id: 'matched', label: t('fuel.cards.tx.filters.matched') },
              { id: 'anomaly', label: t('fuel.cards.tx.filters.anomaly') },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setTxFilter(f.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  txFilter === f.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <span className="text-sm text-gray-500">{t('common.pagination.results', { count: filteredTx.length })}</span>
        </div>

        {/* Transaction list */}
        {filteredTx.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{txFilter !== 'all' ? t('fuel.cards.tx.emptyFiltered') : t('fuel.cards.tx.empty')}</p>
            <p className="text-sm mt-1">{t('fuel.cards.tx.emptyHint')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.tx.table.date')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.tx.table.station')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.tx.table.vehicle')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.tx.table.fuel')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.tx.table.liters')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.tx.table.amount')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.tx.table.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTx.slice(0, 100).map(tx => {
                  const recon = RECONCILIATION_STATUS[tx.reconciliation_status] || RECONCILIATION_STATUS.unmatched;
                  const ReconIcon = recon.icon;
                  return (
                    <tr key={tx.id} className={`hover:bg-gray-50 ${tx.is_anomaly ? 'bg-red-50' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {tx.transaction_date ? fmt.date(tx.transaction_date) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-gray-900">{tx.station_name || '-'}</div>
                        {tx.station_city && <div className="text-xs text-gray-500">{tx.station_city}</div>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{tx.vehicle_registration || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{tx.fuel_type || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right font-mono">{tx.liters ? t('fuel.cards.tx.litersValue', { value: fmt.number(tx.liters, { decimals: 1 }) }) : '-'}</td>
                      <td className="px-4 py-3 text-sm text-right font-mono font-medium">{tx.amount ? money(tx.amount, tx) : '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-medium ${recon.color}`}>
                          <ReconIcon className="w-3 h-3" />
                          {t(`fuel.cards.reconciliation.${recon.code}`)}
                        </span>
                        {tx.is_anomaly && (
                          <div className="text-xs text-red-600 mt-0.5" title={tx.anomaly_reason}>
                            <AlertTriangle className="w-3 h-3 inline" /> {t('fuel.cards.tx.anomaly')}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredTx.length > 100 && (
              <div className="p-3 text-center text-sm text-gray-500 bg-gray-50">
                {t('fuel.cards.tx.truncated', { limit: 100, total: filteredTx.length })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // IMPORT TAB
  // =========================================================================
  function renderImportTab() {
    return (
      <div className="space-y-4">
        {/* Import form */}
        <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-500" />
            {t('fuel.cards.import.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.import.providerLabel')}</label>
              <select
                value={importProvider}
                onChange={e => setImportProvider(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">{t('fuel.cards.form.providerPlaceholder')}</option>
                {Object.keys(PROVIDER_CONFIG).map(k => (
                  <option key={k} value={k}>{t(`fuel.cards.providers.${k}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.cards.import.fileLabel')}</label>
              <input
                type="file"
                accept=".csv"
                onChange={e => setImportFile(e.target.files[0])}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleImport}
                disabled={importing || !importFile || !importProvider}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 disabled:opacity-50"
              >
                {importing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {importing ? t('fuel.cards.import.importing') : t('common.actions.import')}
              </button>
            </div>
          </div>

          {/* Import result */}
          {importResult && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">{t('fuel.cards.import.resultTitle')}</h4>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div><span className="text-gray-600">{t('fuel.cards.import.totalRows')}</span> <span className="font-medium">{importResult.total_rows}</span></div>
                <div><span className="text-gray-600">{t('fuel.cards.import.imported')}</span> <span className="font-medium text-green-700">{importResult.imported}</span></div>
                <div><span className="text-gray-600">{t('fuel.cards.import.skipped')}</span> <span className="font-medium text-orange-600">{importResult.skipped}</span></div>
                <div><span className="text-gray-600">{t('fuel.cards.import.errors')}</span> <span className="font-medium text-red-600">{importResult.errors}</span></div>
              </div>
              {/* Ce que l'import a produit au-delà des transactions : conversion de devise,
                  pleins rattachés, services (péage, lavage, recharge) versés en dépenses. */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3 pt-3 border-t border-green-200">
                <div><span className="text-gray-600">{t('fuel.cards.import.subsidiaryCurrency')}</span> <span className="font-medium">{importResult.currency || '—'}</span></div>
                <div><span className="text-gray-600">{t('fuel.cards.import.convertedRows')}</span> <span className="font-medium">{importResult.converted_rows || 0}</span></div>
                <div><span className="text-gray-600">{t('fuel.cards.import.fuelLogsCreated')}</span> <span className="font-medium">{importResult.fuel_logs_created || 0}</span></div>
                <div><span className="text-gray-600">{t('fuel.cards.import.expensesCreated')}</span> <span className="font-medium">{importResult.expenses_created || 0}</span></div>
              </div>
              {importResult.converted_rows > 0 && (
                <p className="text-xs text-gray-600 mt-2">
                  {t('fuel.cards.import.conversionNote', { currency: importResult.currency })}
                </p>
              )}
              {importResult.date_range?.start && (
                <p className="text-sm text-gray-600 mt-2">
                  {t('fuel.cards.import.period', { start: importResult.date_range.start, end: importResult.date_range.end })}
                </p>
              )}
            </div>
          )}

          {/* Tips */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-1">{t('fuel.cards.import.formatsTitle')}</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li><strong>TotalEnergies</strong> — {t('fuel.cards.import.formats.totalenergies')}</li>
              <li><strong>DKV</strong> — {t('fuel.cards.import.formats.dkv')}</li>
              <li><strong>Shell</strong> — {t('fuel.cards.import.formats.shell')}</li>
              <li><strong>{t('fuel.cards.providers.other')}</strong> — {t('fuel.cards.import.formats.other')}</li>
            </ul>
          </div>
        </div>

        {/* Import history */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            {t('fuel.cards.history.title')}
          </h3>
          {importHistory.length === 0 ? (
            <p className="text-sm text-gray-500">{t('fuel.cards.history.empty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.history.table.date')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.history.table.provider')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.history.table.file')}</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.history.table.imported')}</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.history.table.duplicates')}</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.history.table.errors')}</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">{t('fuel.cards.history.table.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {importHistory.map(imp => {
                  const prov = PROVIDER_CONFIG[imp.provider] || PROVIDER_CONFIG.other;
                  return (
                    <tr key={imp.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm">{imp.created_at ? fmt.date(imp.created_at) : '-'}</td>
                      <td className="px-4 py-2 text-sm">
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${prov.color}`}>{t(`fuel.cards.providers.${prov.code}`)}</span>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">{imp.filename || '-'}</td>
                      <td className="px-4 py-2 text-sm text-right font-medium text-green-700">{imp.imported_rows || 0}</td>
                      <td className="px-4 py-2 text-sm text-right text-orange-600">{imp.skipped_rows || 0}</td>
                      <td className="px-4 py-2 text-sm text-right text-red-600">{imp.error_rows || 0}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          imp.status === 'completed' ? 'bg-green-100 text-green-800' :
                          imp.status === 'partial' ? 'bg-orange-100 text-orange-800' :
                          imp.status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {t(`fuel.cards.history.status.${imp.status}`, { defaultValue: imp.status })}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // =========================================================================
  // STATS TAB — enriched with consumption breakdown + alerts
  // =========================================================================
  function renderStatsTab() {
    if (!stats) return <div className="text-center text-gray-500 py-8">{t('fuel.cards.stats.loading')}</div>;

    // Consumption alerts: cards at >= 70% of limit
    const alerts = consumption
      .filter(c => c.monthly_limit > 0 && c.consumed_pct >= 70)
      .sort((a, b) => (b.consumed_pct || 0) - (a.consumed_pct || 0));

    // Total budget: sum of all limits vs sum consumed
    const totalLimit = consumption.reduce((s, c) => s + (c.monthly_limit || 0), 0);
    const totalConsumed = consumption.reduce((s, c) => s + (c.consumed || 0), 0);
    const totalPct = totalLimit > 0 ? Math.round((totalConsumed / totalLimit) * 100) : null;

    return (
      <div className="space-y-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">{t('fuel.cards.stats.activeCards')}</div>
            <div className="text-2xl font-bold text-blue-600">{stats.cards?.active || 0}</div>
            <div className="text-xs text-gray-400">{t('fuel.cards.stats.ofTotalCards', { count: stats.cards?.total || 0 })}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">{t('fuel.cards.stats.totalSpend')}</div>
            <div className="text-2xl font-bold text-green-600">{money(stats.transactions?.total_spend || 0, cardsCurrency)}</div>
            <div className="text-xs text-gray-400">{t('fuel.cards.stats.litersCount', { count: Math.round(stats.transactions?.total_liters || 0) })}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">{t('fuel.cards.stats.monthlyBudget')}</div>
            <div className="text-2xl font-bold text-blue-700">{totalPct != null ? `${totalPct}%` : '—'}</div>
            <div className="text-xs text-gray-400">{money(totalConsumed, cardsCurrency)} / {money(totalLimit, cardsCurrency)}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">{t('fuel.cards.stats.unmatched')}</div>
            <div className="text-2xl font-bold text-orange-600">{stats.transactions?.unmatched || 0}</div>
            <div className="text-xs text-gray-400">{t('fuel.cards.stats.ofTotalTx', { count: stats.transactions?.total || 0 })}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-gray-500 mb-1">{t('fuel.cards.stats.anomalies')}</div>
            <div className="text-2xl font-bold text-red-600">{stats.transactions?.anomalies || 0}</div>
            <div className="text-xs text-gray-400">{t('fuel.cards.stats.toCheck')}</div>
          </div>
        </div>

        {/* Consumption alerts */}
        {alerts.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              {t('fuel.cards.stats.alertsTitle', { count: alerts.length })}
            </h3>
            <div className="space-y-3">
              {alerts.map(c => {
                const prov = PROVIDER_CONFIG[c.provider] || PROVIDER_CONFIG.other;
                return (
                  <div key={c.card_id} className="flex items-center gap-4">
                    <CreditCard className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="font-mono text-sm w-40 truncate">{c.card_number}</span>
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium ${prov.color}`}>{t(`fuel.cards.providers.${prov.code}`)}</span>
                    <div className="flex-1">
                      <ConsumptionGauge consumed={c.consumed} limit={c.monthly_limit} row={c} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* By provider */}
        {stats.by_provider && Object.keys(stats.by_provider).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('fuel.cards.stats.byProvider')}</h3>
            <div className="space-y-3">
              {Object.entries(stats.by_provider).map(([provider, data]) => {
                const prov = PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.other;
                const pct = stats.transactions?.total_spend > 0
                  ? (data.amount / stats.transactions.total_spend * 100)
                  : 0;
                return (
                  <div key={provider} className="flex items-center gap-4">
                    <span className={`inline-flex px-2 py-0.5 text-xs rounded-full font-medium w-32 justify-center ${prov.color}`}>
                      {t(`fuel.cards.providers.${prov.code}`)}
                    </span>
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-28 text-right">{money(data.amount, cardsCurrency)}</span>
                    <span className="text-xs text-gray-500 w-16 text-right">{t('fuel.cards.stats.txCount', { count: data.count })}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Price per liter + all cards consumption */}
        <div className={`grid grid-cols-1 ${stats.transactions?.avg_price_per_liter ? 'md:grid-cols-2' : ''} gap-4`}>
          {stats.transactions?.avg_price_per_liter > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Fuel className="w-5 h-5 text-green-600" />
                {t('fuel.cards.stats.avgPriceTitle')}
              </h3>
              <div className="text-3xl font-bold text-green-700">
                {t('fuel.cards.stats.avgPriceValue', {
                  price: fmt.number(stats.transactions.avg_price_per_liter, { decimals: 3 }),
                  symbol: currencySymbol(cardsCurrency)
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('fuel.cards.stats.avgPriceHint', { count: stats.transactions?.total || 0 })}</p>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('fuel.cards.stats.monthConsumption')}</h3>
            {totalLimit > 0 ? (
              <>
                <ConsumptionGauge consumed={totalConsumed} limit={totalLimit} />
                <p className="text-xs text-gray-400 mt-2">{t('fuel.cards.stats.remaining', { amount: money(Math.max(0, totalLimit - totalConsumed), cardsCurrency) })}</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">{t('fuel.cards.stats.noLimitSet')}</p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default FuelCardView;
